import * as React from 'react';
import { flow, forEach, get } from 'lodash';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../redux/IStore';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { IAuthState } from '../../../../../../services/auth/models';
import { destroy, reset } from 'redux-form';
import { IMtaActions, IMtaFormNames, IMtaState } from '../../../../../../services/mta/models';
import { mtaActions } from '../../../../../../services/mta/actions';
import Loading from '../../../../../../components/Loading';
import { withJob } from 'react-jobs';
import { actions as brandingActions } from '../../../../../../services/branding/reducer';
import { IBrandingActions, IBrandingState } from '../../../../../../services/branding/models';
import { ICustomerMTAOptions } from '../../index';
import Heap from '../../../../../../helpers/Heap';
import { isLuckyTripProduct } from '../../../../../../helpers/TravelApplication';
import { differenceInDays, parse } from 'date-fns';
import * as api from '../../../../../../data/api/api';
import { IPolicyClaims } from '../../../../../../data/api/models';
import { RedirectCommon } from '../RedirectCommon';

interface IMakeAChangeProps {
  auth: IAuthState;
  branding: IBrandingState;
  mta: IMtaState;
  customerMTAOptions: ICustomerMTAOptions;
  match: any;
  actions: {
    push(path: string): void;
    reset(formName: string): void;
    destroy(formName: string): void;
    mta: IMtaActions;
    branding: IBrandingActions;
  };
}

interface IMakeAChangeSelectionState {
  allowedMta: boolean;
  loading: boolean;
}

class RedirectToMtaUpsell extends React.Component<IMakeAChangeProps, IMakeAChangeSelectionState> {

  constructor(props: IMakeAChangeProps) {
    super(props);
    this.state = {
      allowedMta: true,
      loading: false,
    };
  }

  componentDidMount() {
    const { match, actions, customerMTAOptions } = this.props;
    this.setState({ loading: true });
    this.resetForms();
    const promises = [];

    promises.push(api.getCustomerRateableClaims())
    promises.push(actions.mta.setMTACancellationOptions(get(customerMTAOptions, 'cancellation')));
    promises.push(actions.mta.setUpsellRules(false));

    Promise.all(promises).then((results) => {
      // Retrieve the claims from getCustomerRateableClaims.
      const claims = results[0]
      // Find any claims related to this policy
      const policyClaims = claims.filter((claim: IPolicyClaims) => claim?.attributes?.policy_id === match.params.id)

      policyClaims.forEach((claim: IPolicyClaims) => {
        // Open claims will prevent all MTA.
        if (get(claim, 'id', null) !== null && get(claim, 'attributes.claim_status') === 'open') {
          this.setState({ allowedMta: false })
          this.setState({ loading: false });
          // If any are open, we can exit here with MTA disabled
          return;
        }
      })

      this.setSelectedPolicy(match.params.id, true);
    }).catch((error) => {
      this.setState({ loading: false });
    });
  }

  private handleRedirectToUpsellForm = () => {
    this.resetForms();
    this.props.actions.push(`/account/make-a-change/amt-upsell`);
  }

  private resetForms = () => {
    const { actions } = this.props;
    forEach(IMtaFormNames, (value, key) => {
      actions.reset(value);
      actions.destroy(value);
    });
    actions.mta.resetMtaData();
  }

  private setSelectedPolicy = (policyId, track = false): void => {
    const { auth, actions } = this.props;
    const { allowedMta } = this.state;
    let selectedPolicy = null;
    if (get(auth, 'customer.activePolicies', []).length) {

      forEach(auth.customer.activePolicies, (policySelected) => {
        if (policySelected.id === policyId) {
          selectedPolicy = policySelected;
        }
      });

      if (selectedPolicy && !isLuckyTripProduct(selectedPolicy)) {
        Promise.resolve(actions.mta.setSelectedPolicy(selectedPolicy)).then(() => {
          actions.mta.getPolicyInfo();
          const canUpsellPolicy = this.canUpsellPolicy();
          Promise.resolve(actions.mta.setUpsellRules(canUpsellPolicy)).then(() => {
            this.setState({ loading: false });
            if (track) {
              Heap.trackEvent('MtaJourneyStart', selectedPolicy, 'policy')
            }

            if (canUpsellPolicy && allowedMta) {
              this.handleRedirectToUpsellForm()
            }
          })
        });
        return;
      } else {
        this.props.actions.push(`/account/policies/${policyId}`);
      }

    }
  }

  private canUpsellPolicy = (): boolean => {
    const { mta, customerMTAOptions } = this.props;
    const isSingle = get(mta, 'selectedPolicy.metadata.scheme_type', '') === 'single';
    const isPolicyNotExpired = new Date() < parse(get(mta, 'selectedPolicy.expiresAt'), 'dd/MM/yyyy', new Date())
    const selectedPolicyMeta = get(mta, 'selectedPolicy.metadata');
    const tripDuration = differenceInDays(new Date(get(selectedPolicyMeta, 'end_date')), new Date(get(selectedPolicyMeta, 'start_date')))
    const canShowUpsellOption = get(customerMTAOptions, 'change_trip_type.enabled', false) && tripDuration <= 30
    return (canShowUpsellOption && isSingle && isPolicyNotExpired);
  }

  public render() {
    const { mta } = this.props;
    const { loading, allowedMta } = this.state;

    if (mta.isLoading || loading) {
      return (
        <Loading/>
      )
    }

    if (!allowedMta && !loading) {
      return RedirectCommon.renderNotAllowedMTAStatic()
    }

    return (
      <Loading/>
    )
  }
}

export default flow([
  withJob({
    work: (props: IMakeAChangeProps) => {
      const promises = [];

      promises.push(props.actions.branding.reLoadChannel(props.branding.defaultChannelKey));

      return Promise.all(promises);
    },
    LoadingComponent: (props) => <Loading/>,
    ErrorComponent: ({ error }) => {

      return (
        <div>
          {error.message}
        </div>
      );
    },
  }),
  connect(
    (state: IStore) => {
      return {
        auth: state.auth,
        mta: state.mta,
        branding: state.branding,
        customerMTAOptions: get(state.branding.channel.products[0], 'metadata.direct_customer_mta_options', null),
      };
    },
    (dispatch) => ({
      actions: {
        push: bindActionCreators(push, dispatch),
        reset: bindActionCreators(reset, dispatch),
        destroy: bindActionCreators(destroy, dispatch),
        mta: bindActionCreators({ ...mtaActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
      },
    }),
  ),
])(RedirectToMtaUpsell);
